import React, {useState, useRef, useLayoutEffect} from 'react';
import styled from 'styled-components';
import {FixtureElement} from "../../../../models/api/codegen/code";
import {useDispatch} from "react-redux";
import {addFormatDate} from "../../../../utils/FormatDate";
import {IconButton} from "../../../elements";
import {SET_WIDGET_ID} from "../../../../store/spirs/actions";
import {LiveOrLineType} from "../../../../store/spirs/types";
import {WidgetIcon} from "../../../WidgetsPane/WidgetShowButton";
import {Desktop} from "../../../adaptive/Desktop";
import {Mobile} from "../../../adaptive/Mobile";


const GameStatusWrapper = styled.div`
    display: flex;
    padding-top: 7px;
    padding-bottom: 6px;
    padding-right: 7px;
    background-color: #171A1E; 
`;
GameStatusWrapper.displayName = 'GameStatusWrapper';

const FavoritesWrapper = styled.div`
    display: flex;
    flex-direction: column;
    padding-right: 17px;
    justify-content: space-evenly;
`;
FavoritesWrapper.displayName = 'FavoritesWrapper';


const GameStatusText = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: space-evenly;
    flex-grow: 1;
    overflow: hidden;
`;
GameStatusText.displayName = 'GameStatusText';
const FlexContainer = styled.div`
    display: flex;
    justify-content: space-between;
    font-size: 14px;
    font-weight:500;
    opacity: 0.9;
    letter-spacing:0.89;
`;

// @ts-ignore
const FlexContainerSecondary = styled(FlexContainer)`
    color: #ababab;
`;

const ScoreContainer = styled.span`
    color: #ffdb4d;
    padding-right: 12px;
`;

const StyledTruncatedText = styled.span`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;
  font-size: 16px;
  position: relative;
  margin-left: 16px;
`;

type GameStatusProps = {
    callback?: any;
    fixture: FixtureElement;
    onClick: (withWidget: boolean) => void;
    onClickMobile: (withWidget: boolean) => void;
    mode: LiveOrLineType;
    color: string;
    isChessLive: boolean
};

const GameStatus = (props: GameStatusProps) => {
    const {fixture} = props;
    const [isTruncated, setIsTruncated] = useState(false);
    const textRef = useRef<HTMLSpanElement>(null);

    const onClick = () => props.onClick(false);

    const onClickMobile = () => props.onClickMobile(false);

    const title = React.useMemo(function () {
        return fixture.cn ? fixture.cn.replace("vs.", " - ") : "";
    }, [fixture.cn])

    useLayoutEffect(() => {
        const check = () => {
            if (textRef.current) {
            const el = textRef.current;
            if (el.offsetWidth > 0) {
                setIsTruncated(el.scrollHeight > el.clientHeight);
            }
            }
        };

        check();
        const fallback = setTimeout(check, 100);
        window.addEventListener('resize', check);
        return () => {
            clearTimeout(fallback);
            window.removeEventListener('resize', check);
        }
    }, [title]);
    

    return (
        <>
            <div className="block tablet:hidden">
                <div>
                    <span
                    onClick={onClickMobile}
                    className="block text-[10px] font-normal tracking-[0.778px] text-[#fff] cursor-pointer overflow-hidden text-ellipsis hover:underline mr-[13px]"
                    title={title}
                    >
                    {title}
                    </span>
                </div>
                <MatchDateInfo
                    fixture={fixture}
                    onClick={props.onClick}
                    onClickMobile={props.onClickMobile}
                    mode={props.mode}
                    color={props.color}
                    isChessLive={props.isChessLive}
                />
            </div>
            <div className="hidden tablet:block netbook:!hidden">
                <span
                    onClick={onClick}
                    className="cursor-pointer overflow-hidden text-ellipsis hover:underline text-base ml-4 text-[11px] font-normal"
                    title={title}
                >
                    {title}
                </span>
            </div>
            <div className="hidden netbook:!block">
                <div className="relative max-w-full group">
                    <StyledTruncatedText
                    ref={textRef}
                    onClick={onClick}
                    >
                    {title}
                    </StyledTruncatedText>
                    {isTruncated && (
                    <div className="absolute top-0 left-0 bg-[#252930] text-[#42B6F6] ml-1 px-2 rounded text-[16px] min-h-[47px] z-10 max-w-[407px] w-max line-clamp-2 overflow-hidden pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                        {title}
                    </div>
                    )}
                </div>
            </div>
        </>
    );
}; 

export const MatchDateInfo: React.FC<GameStatusProps> = (props) => {
    const {fixture, isChessLive} = props;
    const dispatch = useDispatch();

    const parsedScore = React.useMemo(function () {
        if (fixture.s === 'NOT_STARTED' || isChessLive) {
            return '';
        }

        if (!fixture.ms || fixture.ms.homeScore === null || fixture.ms.awayScore === null) {
            return '';
        }

        return fixture.ms && (fixture.ms.homeScore + ':' + fixture.ms.awayScore);
    }, [fixture.ms, fixture.s])

    const handleClickOnWidget = () => {
        dispatch(SET_WIDGET_ID({
            widgetId: fixture.fw,
            configurationId: fixture.wci
        }));
        props.onClick(true);
    }

    const handleClickOnWidgetMobile = () => {
        dispatch(SET_WIDGET_ID({
            widgetId: fixture.fw,
            configurationId: fixture.wci
        }));
        props.onClickMobile(true);
    }
    const startTime = React.useMemo(function () {
        return (props.mode === "LINE" || props.mode === "NOT_STARTED") && addFormatDate(fixture.st);
    }, [props.mode, fixture.st])

    const matchStatus = React.useMemo(function () {
        return props.mode === "LIVE" && fixture.ms && fixture.ms.matchStatus;
    }, [props.mode, fixture.ms])

    const timeForSpecialLine = React.useMemo(function () {
        if (!fixture.ms || !fixture.ms.eventClock || !fixture.ms.eventClock.eventTime) {
            return '';
        }

        let time = fixture.ms.eventClock.eventTime;
        if (time === null) {
            time = ''
        }
        return fixture.ms && fixture.ms.eventClock && ` ` + time;
    }, [fixture.ms])

    return (
        <div className="text-[#ABABAB] tablet:w-[60px] text-[11px] text-left tablet:text-right">
            <span className="block tablet:hidden text-[11px]" style={{color: fixture.sc}}>{fixture.fi}</span>
            <div>
                <span>{startTime}</span>
                <span>{matchStatus}</span>
                <span>{timeForSpecialLine}</span>
            </div>
            <div>
                {parsedScore.length > 0 && <ScoreContainer>{parsedScore}</ScoreContainer>}
                {fixture.fw ? (<div>
                    <Desktop>
                        <IconButton className="px-[12px]" onClick={handleClickOnWidget}>
                            <WidgetIcon fixtureWidgetId={fixture.fw}/>
                        </IconButton>
                    </Desktop>
                    <Mobile>
                        <IconButton className="px-[12px]" onClick={handleClickOnWidgetMobile}>
                            <WidgetIcon fixtureWidgetId={fixture.fw}/>
                        </IconButton>
                    </Mobile>
                </div>) : <React.Fragment/>}
            </div>
        </div>
    )
}

export const MemoGameStatus = React.memo(GameStatus, (p, n) => {
    return p.fixture === n.fixture
})
